import {
    BarChart3,
    Calendar,
    FileText,
    FolderTree,
    LayoutDashboard,
    Settings,
    Sparkles,
    Users,
    Users2,
    Wallet,
    Tag,
} from "lucide-react"
import { PermissionAction, PermissionResource, UserRole } from "@/lib/types/auth.types"

import type { LucideIcon } from "lucide-react"

export interface MenuItem {
    id: string
    label: string
    icon?: LucideIcon
    path: string
    resource: PermissionResource
    action: PermissionAction
    children?: MenuItem[]
    // Roles that can access this menu item
    allowedRoles?: UserRole[]
}

// Role-based permissions configuration
export const ROLE_PERMISSIONS: Record<UserRole, Array<{ resource: PermissionResource; actions: PermissionAction[] }>> = {
    superadmin: [
        { resource: "dashboard", actions: ["view"] },
        { resource: "reports", actions: ["view"] },
        { resource: "settings", actions: ["view", "create", "edit", "delete"] },
        { resource: "overview", actions: ["view"] },
        { resource: "appointments", actions: ["view", "create", "edit", "delete"] },
        { resource: "services", actions: ["view", "create", "edit", "delete"] },
        { resource: "customers", actions: ["view", "create", "edit", "delete"] },
        { resource: "users", actions: ["view", "create", "edit", "delete"] },
        { resource: "wallets", actions: ["view", "edit"] },
        { resource: "content", actions: ["view", "create", "edit", "delete"] },
        { resource: "discounts", actions: ["view", "create", "edit", "delete"] },
    ],
    admin: [
        { resource: "dashboard", actions: ["view"] },
        { resource: "reports", actions: ["view"] },
        { resource: "settings", actions: ["view", "edit"] },
        { resource: "overview", actions: ["view"] },
        { resource: "appointments", actions: ["view", "create", "edit"] },
        { resource: "services", actions: ["view", "create", "edit"] },
        { resource: "customers", actions: ["view", "create", "edit"] },
        { resource: "users", actions: ["view", "create", "edit"] },
        { resource: "wallets", actions: ["view", "edit"] },
        { resource: "content", actions: ["view", "create", "edit", "delete"] },
        { resource: "discounts", actions: ["view", "create", "edit", "delete"] },
    ],
    manager: [
        { resource: "dashboard", actions: ["view"] },
        { resource: "reports", actions: ["view"] },
        { resource: "settings", actions: ["view"] },
        { resource: "overview", actions: ["view"] },
        { resource: "appointments", actions: ["view", "create", "edit"] },
        { resource: "services", actions: ["view", "create", "edit"] },
        { resource: "customers", actions: ["view"] },
        { resource: "users", actions: ["view"] },
        { resource: "wallets", actions: ["view", "edit"] },
    ],
    marketer: [
        { resource: "dashboard", actions: ["view"] },
        { resource: "settings", actions: ["view"] },
        { resource: "overview", actions: ["view"] },
        { resource: "appointments", actions: ["view", "create"] },
        { resource: "customers", actions: ["view"] },
        { resource: "discounts", actions: ["view", "create", "edit", "delete"] },
    ],
    finance: [
        { resource: "dashboard", actions: ["view"] },
        { resource: "reports", actions: ["view"] },
        { resource: "overview", actions: ["view"] },
        { resource: "appointments", actions: ["view"] },
        { resource: "services", actions: ["view"] },
        { resource: "customers", actions: ["view"] },
        { resource: "users", actions: ["view"] },
        { resource: "wallets", actions: ["view", "edit"] },
    ],
    creator: [
        { resource: "dashboard", actions: ["view"] },
        { resource: "overview", actions: ["view"] },
        { resource: "content", actions: ["view", "create", "edit"] },
    ],
    non_essential: [
        { resource: "dashboard", actions: ["view"] },
        { resource: "overview", actions: ["view"] },
        { resource: "appointments", actions: ["view"] },
        { resource: "customers", actions: ["view"] },
    ],
    customer: [
        { resource: "dashboard", actions: ["view"] },
        { resource: "reports", actions: ["view"] },
        { resource: "overview", actions: ["view"] },
        { resource: "appointments", actions: ["view", "create"] },
        { resource: "services", actions: ["view"] },
        { resource: "customers", actions: ["view"] },
        { resource: "users", actions: ["view"] },
    ],
}

export const mainMenu: MenuItem[] = [
    {
        id: "dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
        path: "/business/dashboard",
        resource: "dashboard",
        action: "view",
    },
    {
        id: "reports",
        label: "Reports",
        icon: BarChart3,
        path: "/business/reports",
        resource: "reports",
        action: "view",
    },
    {
        id: "discounts",
        label: "Discounts",
        icon: Tag,
        path: "/business/discounts",
        resource: "discounts",
        action: "view",
        allowedRoles: ["superadmin", "admin", "marketer"],
    },
    {
        id: "settings",
        label: "Settings",
        icon: Settings,
        path: "/business/settings/general",
        resource: "settings",
        action: "view",
    },
]

export const secondaryMenu: MenuItem[] = [
    {
        id: "overview",
        label: "Overview",
        icon: Sparkles,
        path: "/business/dashboard",
        resource: "overview",
        action: "view",
    },
    {
        id: "appointments",
        label: "Appointments",
        icon: Calendar,
        path: "/business/appointments",
        resource: "appointments",
        action: "view",
    },
    {
        id: "services",
        label: "Services",
        icon: Sparkles,
        path: "/business/services",
        resource: "services",
        action: "view",
    },
    {
        id: "service-categories",
        label: "Categories",
        icon: FolderTree,
        path: "/business/service-categories",
        resource: "services",
        action: "view",
    },
    {
        id: "content",
        label: "Content",
        icon: FileText,
        path: "/business/content",
        resource: "content",
        action: "view",
        allowedRoles: ["superadmin", "admin", "creator"],
    },
    {
        id: "customers",
        label: "Customers",
        icon: Users2,
        path: "/business/customers",
        resource: "customers",
        action: "view",
    },
    {
        id: "wallets",
        label: "Wallets",
        icon: Wallet,
        path: "/business/wallets",
        resource: "wallets",
        action: "view",
        allowedRoles: ["superadmin", "admin", "manager", "finance"],
    },
    {
        id: "users",
        label: "Users",
        icon: Users,
        path: "/business/users",
        resource: "users",
        action: "view",
        allowedRoles: ["superadmin", "admin", "manager"],
    },
]
